import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { BarChart3, Target, Brain, TrendingUp, BookOpen, Clock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface DifficultyAnalysis {
  suggestedLevel: number;
  confidence: number;
  factors: {
    vocabularyComplexity: number;
    conceptualDifficulty: number;
    prerequisiteKnowledge: number;
    questionStructure: number;
    cognitiveLoad: number;
  };
  reasoning: string[];
  comparisons: {
    level: number;
    description: string;
    examples: string[];
  }[];
  recommendations: string[];
}

export default function DifficultyAnalyzer() {
  const { toast } = useToast();
  const [content, setContent] = useState("");
  const [contentType, setContentType] = useState("mcq");
  const [targetLevel, setTargetLevel] = useState("3");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState<DifficultyAnalysis | null>(null);

  // Mock recent analyses
  const recentAnalyses = [
    {
      id: "1",
      title: "Cardiac Physiology MCQ",
      suggested: 4,
      current: 3,
      type: "MCQ",
      date: "1 hour ago"
    },
    {
      id: "2",
      title: "Basic Anatomy Notes",
      suggested: 2,
      current: 2,
      type: "Note",
      date: "2 hours ago"
    },
    {
      id: "3",
      title: "Advanced Pharmacology",
      suggested: 5,
      current: 4,
      type: "MCQ",
      date: "4 hours ago"
    }
  ];

  const difficultyLevels = [
    { value: "1", label: "Beginner (1)", description: "Basic concepts, simple terminology" },
    { value: "2", label: "Elementary (2)", description: "Fundamental knowledge, clear explanations" },
    { value: "3", label: "Intermediate (3)", description: "Standard medical knowledge" },
    { value: "4", label: "Advanced (4)", description: "Complex concepts, advanced terminology" },
    { value: "5", label: "Expert (5)", description: "Specialized knowledge, research-level" }
  ];

  const analyzeContent = async () => {
    if (!content.trim()) {
      toast({
        title: "No Content",
        description: "Please enter content to analyze.",
        variant: "destructive"
      });
      return;
    }

    setIsAnalyzing(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('ai-difficulty-analyzer', {
        body: {
          content: content,
          contentType: contentType,
          targetLevel: parseInt(targetLevel)
        }
      });

      if (error) throw error;
      
      setAnalysis(data.analysis);
      
      toast({
        title: "Analysis Complete",
        description: "Difficulty analysis has been completed successfully.",
      });
    } catch (error) {
      console.error('Error analyzing difficulty:', error);
      toast({
        title: "Analysis Failed",
        description: "Failed to analyze content difficulty. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const loadSampleContent = () => {
    const samples = {
      mcq: `A 65-year-old patient presents with acute onset chest pain radiating to the left arm, accompanied by diaphoresis and nausea. The ECG shows ST-segment elevation in leads II, III, and aVF. Cardiac enzymes reveal elevated troponin I levels. 

Which of the following is the most likely diagnosis and immediate treatment approach?

A) Unstable angina - administer nitroglycerin and monitor
B) Inferior wall STEMI - initiate primary PCI within 90 minutes
C) Pericarditis - prescribe NSAIDs and colchicine
D) Pulmonary embolism - begin anticoagulation therapy

Correct Answer: B) Inferior wall STEMI - initiate primary PCI within 90 minutes

The clinical presentation of acute chest pain with characteristic radiation, coupled with ST-elevation in the inferior leads (II, III, aVF) and elevated cardiac biomarkers, is diagnostic of an inferior wall ST-elevation myocardial infarction (STEMI). Primary percutaneous coronary intervention (PCI) is the gold standard treatment when available within the recommended time frame.`,
      note: `# Acute Myocardial Infarction: Pathophysiology and Management

## Definition and Classification
Acute myocardial infarction (AMI) represents irreversible myocardial cell death due to prolonged ischemia, typically resulting from thrombotic occlusion of a coronary artery.

### STEMI vs NSTEMI
- **STEMI**: Complete occlusion with transmural infarction
- **NSTEMI**: Partial occlusion with subendocardial infarction

## Pathophysiology
1. **Plaque rupture**: Vulnerable atherosclerotic plaque disruption
2. **Thrombosis**: Platelet aggregation and coagulation cascade activation
3. **Ischemic cascade**: ATP depletion → cellular dysfunction → necrosis

## Diagnostic Criteria
### Universal Definition (4th Edition)
- Elevated cardiac troponins (>99th percentile URL)
- Clinical evidence of acute myocardial ischemia
- Supporting evidence: ECG changes, imaging, angiography`
    };
    
    setContent(samples[contentType as keyof typeof samples]);
  };

  const getDifficultyColor = (level: number) => {
    if (level <= 2) return "text-success";
    if (level === 3) return "text-primary";
    if (level === 4) return "text-warning";
    return "text-destructive";
  };

  const getDifficultyLabel = (level: number) => {
    const labels = ["", "Beginner", "Elementary", "Intermediate", "Advanced", "Expert"];
    return labels[level] || "Unknown";
  };

  const getFactorColor = (score: number) => {
    if (score >= 80) return "text-destructive";
    if (score >= 60) return "text-warning";
    if (score >= 40) return "text-primary";
    return "text-success";
  };

  const getLevelDifference = (suggested: number, target: number) => {
    const diff = suggested - target;
    if (diff === 0) return { text: "Perfect match", color: "text-success" };
    if (Math.abs(diff) === 1) return { 
      text: diff > 0 ? "Slightly harder" : "Slightly easier", 
      color: "text-warning" 
    };
    return { 
      text: diff > 0 ? "Much harder" : "Much easier", 
      color: "text-destructive" 
    };
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium">AI Difficulty Analyzer</h3>
          <p className="text-sm text-muted-foreground">
            Automatically assess and suggest optimal difficulty levels for educational content
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-4 w-4" />
                Difficulty Analysis
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Content Type</label>
                  <Select value={contentType} onValueChange={setContentType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mcq">MCQ Question</SelectItem>
                      <SelectItem value="note">Study Note</SelectItem>
                      <SelectItem value="explanation">Explanation</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium">Target Level</label>
                  <Select value={targetLevel} onValueChange={setTargetLevel}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {difficultyLevels.map((level) => (
                        <SelectItem key={level.value} value={level.value}>
                          {level.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-end">
                  <Button variant="outline" onClick={loadSampleContent} className="w-full">
                    Load Sample
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Content to Analyze</label>
                <Textarea
                  placeholder="Enter your content here for difficulty analysis..."
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  rows={12}
                  className="resize-none"
                />
              </div>

              <Button 
                onClick={analyzeContent} 
                disabled={isAnalyzing || !content.trim()}
                className="w-full flex items-center gap-2"
              >
                <Brain className="h-4 w-4" />
                {isAnalyzing ? "Analyzing..." : "Analyze Difficulty"}
              </Button>
            </CardContent>
          </Card>

          {analysis && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-4 w-4" />
                  Difficulty Analysis Results
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="text-center">
                    <div className={`text-4xl font-bold ${getDifficultyColor(analysis.suggestedLevel)}`}>
                      Level {analysis.suggestedLevel}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      Suggested Difficulty - {getDifficultyLabel(analysis.suggestedLevel)}
                    </p>
                    <div className={`text-sm mt-2 ${getLevelDifference(analysis.suggestedLevel, parseInt(targetLevel)).color}`}>
                      vs Target: {getLevelDifference(analysis.suggestedLevel, parseInt(targetLevel)).text}
                    </div>
                  </div>

                  <div className="text-center">
                    <div className={`text-3xl font-bold ${analysis.confidence >= 80 ? 'text-success' : analysis.confidence >= 60 ? 'text-warning' : 'text-destructive'}`}>
                      {analysis.confidence}%
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      Analysis Confidence
                    </p>
                    <Progress value={analysis.confidence} className="mt-2" />
                  </div>
                </div>

                <Separator />

                <div className="space-y-4">
                  <h4 className="font-medium">Difficulty Factors</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries(analysis.factors).map(([factor, score]) => (
                      <div key={factor} className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="capitalize">
                            {factor.replace(/([A-Z])/g, ' $1').trim()}
                          </span>
                          <span className={getFactorColor(score)}>{score}%</span>
                        </div>
                        <Progress value={score} className="h-2" />
                      </div>
                    ))}
                  </div>
                </div>

                <Separator />

                <div className="space-y-2">
                  <h4 className="font-medium">Analysis Reasoning</h4>
                  <ul className="space-y-1">
                    {analysis.reasoning.map((reason, index) => (
                      <li key={index} className="text-sm text-muted-foreground flex items-start gap-2">
                        <TrendingUp className="h-3 w-3 text-primary mt-0.5 flex-shrink-0" />
                        {reason}
                      </li>
                    ))}
                  </ul>
                </div>

                <Separator />

                <div className="space-y-3">
                  <h4 className="font-medium">Difficulty Level Comparisons</h4>
                  {analysis.comparisons.map((comparison, index) => (
                    <div key={index} className="p-3 border rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant={comparison.level === analysis.suggestedLevel ? "default" : "outline"}>
                          Level {comparison.level}
                        </Badge>
                        <span className="text-sm font-medium">{comparison.description}</span>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Examples: {comparison.examples.join(", ")}
                      </div>
                    </div>
                  ))}
                </div>

                {analysis.recommendations.length > 0 && (
                  <>
                    <Separator />
                    <div className="space-y-2">
                      <h4 className="font-medium text-primary">Recommendations</h4>
                      <ul className="space-y-1">
                        {analysis.recommendations.map((recommendation, index) => (
                          <li key={index} className="text-sm text-muted-foreground flex items-start gap-2">
                            <BookOpen className="h-3 w-3 text-primary mt-0.5 flex-shrink-0" />
                            {recommendation}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        <div>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Recent Analyses
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {recentAnalyses.map((analysis) => (
                <div key={analysis.id} className="p-3 border rounded-lg hover:bg-muted/50 cursor-pointer">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium leading-tight">
                        {analysis.title}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {analysis.type} • {analysis.date}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge variant={analysis.suggested === analysis.current ? "default" : "secondary"}>
                        {analysis.current} → {analysis.suggested}
                      </Badge>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="mt-4">
            <CardHeader>
              <CardTitle className="text-sm">Difficulty Scale</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {difficultyLevels.map((level) => (
                <div key={level.value} className="flex items-center gap-2">
                  <Badge variant={level.value === targetLevel ? "default" : "outline"} className="w-16 justify-center">
                    {level.value}
                  </Badge>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium">{level.label.split(' ')[0]}</p>
                    <p className="text-xs text-muted-foreground">{level.description}</p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}